import React from 'react';
import { Clock, User, Edit, Eye, AlertCircle, MapPin, BookOpen, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SessionsTabNew = ({ sessions, isSessionTerminee, formatDate, formatTime, examen }) => {
  const navigate = useNavigate();
  const safeSessions = Array.isArray(sessions) ? sessions : [];
  
  const handleNoterClick = (sessionId) => {
    navigate(`/examens/sessions/${sessionId}/notes`);
  };

  const handleVoirNotesClick = (sessionId) => {
    navigate(`/examens/sessions/${sessionId}/liste-notes`);
  };

  // Fonction pour calculer la durée entre deux heures
  const calculerDuree = (heureDebut, heureFin) => {
    if (!heureDebut || !heureFin) return 'Non définie';
    
    try {
      // Convertir les heures en format 24h si nécessaire
      const debut = heureDebut.includes(':') ? heureDebut : heureDebut + ':00';
      const fin = heureFin.includes(':') ? heureFin : heureFin + ':00';
      
      // Créer des objets Date pour le calcul
      const [heureD, minuteD] = debut.split(':').map(Number);
      const [heureF, minuteF] = fin.split(':').map(Number);
      
      // Calculer la différence en minutes
      const totalMinutesDebut = heureD * 60 + minuteD;
      const totalMinutesFin = heureF * 60 + minuteF;
      let differenceMinutes = totalMinutesFin - totalMinutesDebut;
      
      // Gérer le cas où l'heure de fin est le lendemain
      if (differenceMinutes < 0) {
        differenceMinutes += 24 * 60;
      }
      
      // Convertir en heures et minutes
      const heures = Math.floor(differenceMinutes / 60);
      const minutes = differenceMinutes % 60;
      
      if (heures === 0) {
        return `${minutes}min`;
      } else if (minutes === 0) {
        return `${heures}h`;
    } else {
        return `${heures}h${minutes.toString().padStart(2, '0')}`;
      }
    } catch (error) {
      return 'Non définie';
    }
  };

  const getSurveillantNom = (surveillant) => {
    if (!surveillant) return 'Non assigné';
    
    // Nouvelle structure avec nom_complet
    if (surveillant.nom_complet) {
      return surveillant.nom_complet;
    }
    
    // Structure avec nom et prenom séparés
    if (surveillant.nom && surveillant.prenom) {
      return `${surveillant.nom} ${surveillant.prenom}`;
    }
    
    // Fallback vers ancienne structure si elle existe encore
    if (surveillant.first_name && surveillant.last_name) {
      return `${surveillant.first_name} ${surveillant.last_name}`;
    }
    
    return 'Non assigné';
  };

  return (
    <div className="bg-white">
      {/* Header moderne avec dégradé */}
      <div className="bg-gradient-to-r from-[#202654] to-[#2a2f6c] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Sessions d'examen</h3>
              <p className="text-white/80 text-sm">
                Planification et suivi des épreuves
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-4 text-sm text-white">
            <div className="text-center">
              <div className="text-xl font-bold">{safeSessions.length}</div>
              <div className="text-white/70">Sessions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu des sessions */}
      <div className="p-6">
        {safeSessions.length > 0 ? (
          <div className="space-y-6">
            {safeSessions.map((session, index) => {
              const surveillantPrincipal = session.surveillant_principal;
              const surveillantSecondaire = session.surveillant_secondaire;
              
            return (
                <div key={session.id || index} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  {/* En-tête de la session avec badge de statut */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-bold text-sm">
                          {index + 1}
                      </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 text-lg">
                            {session.module?.libelle || session.module?.module?.libelle || 'Module non défini'}
                          </h4>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                            <span>{formatDate(session.date_examen)}</span>
                            <span>{formatTime(session.heure_debut)} - {formatTime(session.heure_fin)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {isSessionTerminee(session) ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Terminée
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            ⏳ Programmée
                          </span>
                        )}
                          </div>
                          </div>
                        </div>
                        
                  {/* Corps de la session */}
                  <div className="p-6">
                    {/* Informations principales */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      {/* Salle */}
                      <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                          <MapPin className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Salle</p>
                          <p className="text-sm font-semibold text-blue-900">
                            {session.salle?.libelle || 'Non définie'} 
                            {session.salle?.capacite && (
                              <span className="text-xs text-blue-600 ml-1">
                                ({session.salle.capacite} places)
                              </span>
                            )}
                          </p>
                        </div>
                          </div>

                      {/* Module */}
                      <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100">
                          <BookOpen className="h-4 w-4 text-purple-600" />
                          </div>
                        <div>
                          <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Module</p>
                          <p className="text-sm font-semibold text-purple-900">
                            {session.module?.libelle || session.module?.module?.libelle || 'Non défini'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Durée */}
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
                          <Clock className="h-4 w-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Durée</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {session.duree || calculerDuree(session.heure_debut, session.heure_fin)}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Équipe de surveillance */}
                    {(surveillantPrincipal || surveillantSecondaire) && (
                      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center space-x-2 mb-3">
                          <Users className="h-4 w-4 text-amber-600" />
                          <h5 className="text-sm font-semibold text-amber-800">Équipe de surveillance</h5>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {surveillantPrincipal && (
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800">
                                Principal
                              </span>
                              <span className="text-sm text-gray-700">
                                {getSurveillantNom(surveillantPrincipal)}
                                {surveillantPrincipal.type && (
                                  <span className="text-xs text-gray-500 ml-1">
                                    ({surveillantPrincipal.type})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          {surveillantSecondaire && (
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800">
                                Secondaire
                              </span>
                              <span className="text-sm text-gray-700">
                                {getSurveillantNom(surveillantSecondaire)}
                                {surveillantSecondaire.type && (
                                  <span className="text-xs text-gray-500 ml-1">
                                    ({surveillantSecondaire.type})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Consignes */}
                    {session.consignes && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-600 font-medium mb-1">Consignes particulières</p>
                        <p className="text-sm text-blue-800">{session.consignes}</p>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-600 mb-3 sm:mb-0">
                        <span className="font-medium">Statut:</span>{' '}
                        {isSessionTerminee(session) ? 
                          'Examen terminé - Actions disponibles' : 
                          `En attente - Actions disponibles après le ${formatDate(session.date_examen)}`
                        }
                      </div>
                      
                      <div className="flex space-x-3">
                      {isSessionTerminee(session) ? (
                          <>
                          <button
                            onClick={() => handleNoterClick(session.id)}
                              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
                          >
                            <Edit className="h-4 w-4 mr-2" /> Noter les copies
                          </button>
                          <button
                            onClick={() => handleVoirNotesClick(session.id)}
                              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
                          >
                            <Eye className="h-4 w-4 mr-2" /> Voir les notes
                          </button>
                          </>
                      ) : (
                        <div className="flex items-center text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                          <AlertCircle className="h-4 w-4 mr-2 text-gray-500" />
                          <span>
                            Actions disponibles après le {formatDate(session.date_examen)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full">
              <Clock className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune session programmée</h3>
            <p className="text-gray-600">Les sessions d'examen apparaîtront ici une fois créées.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionsTabNew;
